import {
  decisionLevelValues,
  functionalGroupValues,
  operators as OPERATOR_LABELS,
} from '../../custom-components/QueryEditor';
import General from '../../utils/GeneralUtils';

export type NavigatorSectionId = 'location' | 'expansion' | 'institutions' | 'people';

/**
 * How a filter's value is edited, as the directories' query editor does it: a text input, a
 * number (two for `between`), a select of fixed values, a multi-select of values with typing
 * (`creatable`), a comma list in a textarea, one of the team's territories (`territory`, the
 * directories' single pick) or several (`territories`, as `beacons`), shapes drawn on a map, a
 * radius around an address, the team's beacons, or one number picked on a slider.
 */
export type NavigatorFilterType =
  | 'text'
  | 'number'
  | 'select'
  | 'creatable'
  | 'textarea'
  | 'territory'
  | 'territories'
  | 'map'
  | 'radiusSearch'
  | 'beacons'
  | 'slider';

/** A slider's range and the unit its value is shown in. */
export interface NavigatorSliderRange {
  min: number;
  max: number;
  step: number;
  unit: string;
  /** What the slider at its minimum says, the filter then being off. */
  minLabel: string;
}

export interface NavigatorFilterValue {
  name: string;
  label: string;
}

export interface NavigatorFilter {
  /** The search field the filter sets; the institution ones by their institution-search name. */
  field: string;
  label: string;
  section: NavigatorSectionId;
  /** A row of the panel to begin with; the others come by the section's plus or a row's label. */
  starter?: boolean;
  type: NavigatorFilterType;
  operators: string[];
  defaultOperator: string;
  values?: NavigatorFilterValue[];
  placeholder?: string;
  /** The label of a stored value, for the values a select lists and those typed in. */
  getValue?: (value: string) => string;
  /** A slider's range. */
  range?: NavigatorSliderRange;
  /** The filter this one needs set to mean anything; without it this one is off. */
  requires?: { section: NavigatorSectionId; field: string };
  /** What the row says while its requirement is not set. */
  requiresHint?: string;
  /** The searches take the value as a JSON number, not the stored string. */
  isNumberValue?: boolean;
}

/** The operator's label, as the query editor shows it. */
export const operatorLabel = (operator: string): string =>
  (OPERATOR_LABELS as Record<string, string>)[operator] ?? operator;

/** The operators that take no value. */
export const isValuelessOperator = (operator: string): boolean =>
  operator === 'exists' || operator === 'not_exists';

export interface NavigatorFilterState {
  operator: string;
  /** The rule's value as the query editor stores it: a string, comma-joined for lists. */
  value: string;
  /** Switched off on the panel: kept with its value, but not searched. */
  disabled?: boolean;
}

/** Whether a rule has a value (or needs none): such a rule is kept, switched off or not. */
export const hasFilterValue = (state: NavigatorFilterState | null): boolean =>
  !!state && (isValuelessOperator(state.operator) || state.value.trim() !== '');

/** Whether a rule is part of the search: it has a value and is not switched off. */
export const isFilterSet = (state: NavigatorFilterState | null): boolean =>
  hasFilterValue(state) && !state?.disabled;

/** Whether the filter's requirement, if it has one, is among the set rules. */
export const isRequirementMet = (
  filter: NavigatorFilter,
  rules: ({ section: NavigatorSectionId; field: string } & NavigatorFilterState)[],
): boolean =>
  !filter.requires ||
  rules.some(
    (rule) =>
      rule.section === filter.requires?.section &&
      rule.field === filter.requires.field &&
      isFilterSet(rule),
  );

const labelOf =
  (values: NavigatorFilterValue[]) =>
  (value: string): string =>
    values.find((v) => v.name === value)?.label ?? General.capitalizeAllWords(value);

const INSTITUTION_TYPES: NavigatorFilterValue[] = [
  { name: 'DISTRICT', label: 'District' },
  { name: 'SCHOOL', label: 'School' },
  { name: 'AGENCY', label: 'Service Agency' },
];

const SUBTYPES: NavigatorFilterValue[] = [
  { name: 'REGULAR', label: 'Regular' },
  { name: 'CHARTER', label: 'Charter' },
  { name: 'SPECIALIZED', label: 'Specialized' },
  { name: 'COMPONENT', label: 'Component' },
  { name: 'ALTERNATIVE', label: 'Alternative' },
];

/** The counties, as the query editor offers them: FIPS code → "County (ST)". */
const COUNTIES: NavigatorFilterValue[] = General.getCountiesForSelect();

/**
 * The query's filters, by section: the few the navigator supports, as the directories' query
 * editor defines them (QueryEditor.tsx getFields, environment.ts queryEditorSupportedFields),
 * with the same operators, values and editors. beaconIds and radiusMiles are new: the team's
 * beacons and a radius around them. territoryId takes several territories (`in`, their states
 * together; api/v4/territories.md §12 in the Go repo), where the directories pick one.
 */
export const NAVIGATOR_FILTERS: NavigatorFilter[] = [
  // Location
  {
    field: 'beaconIds',
    label: 'Beacons',
    section: 'location',
    starter: true,
    type: 'beacons',
    operators: ['in'],
    defaultOperator: 'in',
  },
  {
    field: 'territoryId',
    label: 'Territories',
    section: 'location',
    type: 'territories',
    operators: ['in'],
    defaultOperator: 'in',
  },
  {
    field: 'state',
    label: 'State',
    section: 'location',
    starter: true,
    type: 'creatable',
    operators: ['equals', 'not_equals'],
    defaultOperator: 'equals',
    values: General.getStatesForSelect().map((it: { value: string; label: string }) => ({
      name: it.value,
      label: it.label,
    })),
    getValue: (value) => General.getStateName(value) ?? value,
  },
  // The operators both searches take (Go: opsZip; InstitutionZip has does_not_contain too).
  {
    field: 'zip',
    label: 'ZIP code',
    section: 'location',
    type: 'text',
    operators: ['equals', 'starts_with', 'ends_with', 'not_equals'],
    defaultOperator: 'equals',
  },
  // equals only: the institution search takes no other (Go: InstitutionCountyID).
  {
    field: 'countyId',
    label: 'County',
    section: 'location',
    type: 'creatable',
    operators: ['equals'],
    defaultOperator: 'equals',
    values: COUNTIES,
    getValue: labelOf(COUNTIES),
  },

  // Expansion: circles of the radius around the beacons' institutions take the beacons' place
  // (api/v4/beacons.md §12 in the Go repo); the other rules still narrow, so State is a border.
  {
    field: 'radiusMiles',
    label: 'Radius from beacons',
    section: 'expansion',
    starter: true,
    type: 'slider',
    operators: ['equals'],
    defaultOperator: 'equals',
    range: { min: 0, max: 100, step: 5, unit: 'mi', minLabel: 'Beacons only' },
    requires: { section: 'location', field: 'beaconIds' },
    requiresHint: 'Pick a beacon in Location to expand from it',
    isNumberValue: true,
  },

  // Institutions
  {
    field: 'type',
    label: 'Type',
    section: 'institutions',
    starter: true,
    type: 'select',
    operators: ['equals'],
    defaultOperator: 'equals',
    values: INSTITUTION_TYPES,
  },
  {
    field: 'access',
    label: 'Access',
    section: 'institutions',
    starter: true,
    type: 'select',
    operators: ['equals', 'not_equals'],
    defaultOperator: 'equals',
    values: [
      { name: 'PUBLIC', label: 'Public' },
      { name: 'PRIVATE', label: 'Private' },
    ],
  },
  {
    field: 'studentsTotal',
    label: 'Nr. of students',
    section: 'institutions',
    starter: true,
    type: 'number',
    operators: ['gt', 'lt'],
    defaultOperator: 'gt',
  },
  {
    field: 'subtype',
    label: 'Subtype',
    section: 'institutions',
    type: 'creatable',
    operators: ['equals', 'not_equals'],
    defaultOperator: 'equals',
    values: SUBTYPES,
    getValue: labelOf(SUBTYPES),
  },
  // contains and equals: what the people search takes of an institution's name (Go: opsNameLike).
  {
    field: 'name',
    label: 'Name',
    section: 'institutions',
    type: 'text',
    operators: ['contains', 'equals'],
    defaultOperator: 'contains',
  },

  // People
  {
    field: 'role.functionalGroup',
    label: 'Functional group',
    section: 'people',
    starter: true,
    type: 'creatable',
    operators: ['equals', 'not_equals'],
    defaultOperator: 'equals',
    values: functionalGroupValues,
    getValue: labelOf(functionalGroupValues),
  },
  {
    field: 'role.decisionLevel',
    label: 'Decision level',
    section: 'people',
    starter: true,
    type: 'creatable',
    operators: ['equals', 'not_equals'],
    defaultOperator: 'equals',
    values: decisionLevelValues,
    getValue: labelOf(decisionLevelValues),
  },
  // Market theme and Email are left out for the launch (EDU-126, 2026-11-03): a rule on either
  // in an older URL or saved search is dropped by parseNavigatorQuery.
  {
    field: 'title',
    label: 'Title',
    section: 'people',
    type: 'text',
    operators: [
      'contains',
      'equals',
      'does_not_contain',
      'contains_word',
      'starts_with',
      'ends_with',
      'exists',
    ],
    defaultOperator: 'contains',
  },
];

/** A filter's key in the query: the same field is one filter in Institutions and another in People. */
export const filterKey = (filter: NavigatorFilter): string => `${filter.section}.${filter.field}`;
